import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";

// update receipt voucher
export const updateReceiptVoucher = createAsyncThunk(
  "ReceiptVouchers/updateReceiptVoucher",
  async ({ id, voucherData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const isFormData = voucherData instanceof FormData;
      const headers = {
        Authorization: `Bearer ${token}`,
        "Accept-Language": currentLang,
      };

      let url = `${BASE_URL}/receipt-vouchers/${id}`;
      let method = "put";
      let dataToSend = voucherData;

      if (!isFormData) {
        headers["Content-Type"] = "application/json";
        // Ensure details is stringified and total_amount is number
        dataToSend = {
          ...voucherData,
          total_amount:
            voucherData.total_amount !== undefined
              ? Number(voucherData.total_amount)
              : undefined,
          details:
            typeof voucherData.details === "string"
              ? voucherData.details
              : JSON.stringify(voucherData.details || []),
          _method: "PUT", // For backends expecting POST with _method
        };
        // Use POST if backend expects _method: PUT
        method = "post";
      } else {
        // FormData: append _method if needed
        if (!voucherData.has("_method")) {
          voucherData.append("_method", "PUT");
        }
        method = "post";
      }

      const response = await axios[method](url, dataToSend, { headers });
      return response.data;
    } catch (error) {
      let message = "Failed to update receipt voucher!";
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          message = errorData;
        } else if (errorData.message) {
          message = errorData.message;
        } else if (typeof errorData === "object") {
          message = JSON.stringify(errorData);
        }
      }
      return rejectWithValue(message);
    }
  },
);

// get Receipt Vouchers
export const getReceiptVouchers = createAsyncThunk(
  "ReceiptVouchers/getReceiptVouchers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search !== undefined)
        queryParams.append("search", params.search);
      if (params.status) queryParams.append("status", params.status);
      if (params.project_id !== undefined)
        queryParams.append("project_id", params.project_id);
      if (params.account_id !== undefined)
        queryParams.append("account_id", params.account_id);
      if (params.date_from !== undefined) {
        queryParams.append("date_from", params.date_from);
      }
      if (params.date_to !== undefined) {
        queryParams.append("date_to", params.date_to);
      }
      if (params.sort_by) {
        queryParams.append("sort_by", params.sort_by);
      }
      if (params.sort_order) {
        queryParams.append("sort_order", params.sort_order);
      }
      const url = `${BASE_URL}/receipt-vouchers${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get receipt vouchers!";
      return rejectWithValue(message);
    }
  },
);

// get receipt voucher details
export const getReceiptVoucherDetails = createAsyncThunk(
  "ReceiptVouchers/getReceiptVoucherDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.get(`${BASE_URL}/receipt-vouchers/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        "Failed to get receipt voucher details!";
      return rejectWithValue(message);
    }
  },
);

// add receipt voucher
export const addReceiptVoucher = createAsyncThunk(
  "ReceiptVouchers/addReceiptVoucher",
  async (voucherData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const isFormData = voucherData instanceof FormData;
      const headers = {
        Authorization: `Bearer ${token}`,
        "Accept-Language": currentLang,
      };

      if (!isFormData) {
        headers["Content-Type"] = "application/json";
        const payload = {
          ...voucherData,
          total_amount:
            voucherData.total_amount !== undefined
              ? Number(voucherData.total_amount)
              : undefined,
          details:
            typeof voucherData.details === "string"
              ? voucherData.details
              : JSON.stringify(voucherData.details || []),
        };
        const response = await axios.post(
          `${BASE_URL}/receipt-vouchers`,
          payload,
          {
            headers,
          },
        );
        return response.data;
      }

      const response = await axios.post(
        `${BASE_URL}/receipt-vouchers`,
        voucherData,
        {
          headers,
        },
      );
      return response.data;
    } catch (error) {
      let message = "Failed to add receipt voucher!";
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          message = errorData;
        } else if (errorData.message) {
          message = errorData.message;
        } else if (typeof errorData === "object") {
          message = JSON.stringify(errorData);
        }
      }
      return rejectWithValue(message);
    }
  },
);

// post receipt voucher
export const postReceiptVoucher = createAsyncThunk(
  "ReceiptVouchers/postReceiptVoucher",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.post(
        `${BASE_URL}/receipt-vouchers/${id}/post`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to post receipt voucher!";
      return rejectWithValue(message);
    }
  },
);

// delete receipt voucher
export const deleteReceiptVoucher = createAsyncThunk(
  "ReceiptVouchers/deleteReceiptVoucher",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.delete(
        `${BASE_URL}/receipt-vouchers/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete receipt voucher!";
      return rejectWithValue(message);
    }
  },
);
